"use client"

import { useState, useRef, type RefObject, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Download, Loader2 } from "lucide-react"
import { XIcon } from "./x-icon"
import { WarpcastIcon } from "./warpcast-icon"
import { track } from "@vercel/analytics"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog"
import { ShaderButton } from "@/components/shader-button"

interface Collection {
  id: string
  name: string
  image: string
  floorPrice?: number
}

interface ResultsActionsProps {
  containerRef: RefObject<HTMLDivElement>
  collections: Collection[]
  currentCollections: Collection[]
  walletAddress: string
  ensName?: string | null
  isMobile: boolean
  compact?: boolean
}

interface CubeData {
  element: HTMLImageElement
  src: string
  alt: string
  width: number
  height: number
  zIndex: number
  bottomPosition: number
}

interface LoadedImageData {
  img: HTMLImageElement
  x: number
  y: number
  width: number
  height: number
  zIndex: number
}

export function ResultsActions(props: ResultsActionsProps) {
  const { containerRef, collections, currentCollections, walletAddress, ensName, isMobile, compact } = props

  const [isDownloading, setIsDownloading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [shareSuccess, setShareSuccess] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isIOSDevice, setIsIOSDevice] = useState(false)
  const [showDownloadDialog, setShowDownloadDialog] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase()
      const isIOS = /iphone|ipad|ipod/.test(userAgent)
      setIsIOSDevice(isIOS)
    }
    checkDevice()
  }, [])

  // Function to draw a simplified hexagonal watermark with 30-degree rotation
  const addWatermark = (ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number): void => {
    try {
      // Calculate size and position
      const maxSize = Math.min(canvasWidth * 0.08, 120)
      const padding = Math.max(canvasWidth * 0.02, 20)

      // Set transparency for the watermark
      ctx.globalAlpha = 0.5

      // Save current context state
      ctx.save()

      // Position in bottom-right corner
      const x = canvasWidth - maxSize - padding
      const y = canvasHeight - maxSize - padding

      // Move to the center point where we want to draw the hexagon
      ctx.translate(x + maxSize / 2, y + maxSize / 2)

      // Apply 30-degree rotation (convert to radians)
      const rotationAngle = 30 * (Math.PI / 180)
      ctx.rotate(rotationAngle)

      // Brand purple color
      ctx.strokeStyle = "#7B39ED"
      ctx.lineWidth = maxSize * 0.1

      // Draw hexagon
      ctx.beginPath()
      const hexSize = maxSize * 0.45

      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i
        const hx = hexSize * Math.cos(angle)
        const hy = hexSize * Math.sin(angle)
        if (i === 0) {
          ctx.moveTo(hx, hy)
        } else {
          ctx.lineTo(hx, hy)
        }
      }
      ctx.closePath()
      ctx.stroke()

      // Restore the context
      ctx.restore()

      // Reset transparency
      ctx.globalAlpha = 1.0
    } catch (error) {
      console.error("Error creating watermark:", error)
    }
  }

  const generateImage = async (): Promise<string | null> => {
    if (!containerRef.current) return null

    try {
      // Find all cube containers with motion.div elements
      const cubeContainers = containerRef.current.querySelectorAll(".absolute.inset-x-0.mx-auto")

      if (cubeContainers.length === 0) {
        throw new Error("No cube containers found")
      }

      // Collect cube data with proper z-index ordering
      const cubeDataArray: CubeData[] = []

      for (let i = 0; i < cubeContainers.length; i++) {
        const container = cubeContainers[i] as HTMLElement
        const img = container.querySelector("img") as HTMLImageElement

        if (!img) continue

        // Get the computed style to extract the bottom position
        const computedStyle = window.getComputedStyle(container)
        const bottomValue = computedStyle.bottom || "0px"
        const bottomPosition = Number.parseFloat(bottomValue.replace("px", ""))

        // Get actual rendered dimensions
        const rect = img.getBoundingClientRect()

        cubeDataArray.push({
          element: img,
          src: img.src,
          alt: img.alt || `Cube ${i + 1}`,
          width: rect.width,
          height: rect.height,
          zIndex: i, // Lower index = higher z-index (drawn last)
          bottomPosition: bottomPosition,
        })
      }

      // Sort by bottom position (lowest bottom value = highest in tower = drawn last)
      cubeDataArray.sort((a, b) => a.bottomPosition - b.bottomPosition)

      if (cubeDataArray.length === 0) {
        throw new Error("No valid cube images found")
      }

      // Calculate canvas dimensions based on the largest cube
      const maxWidth = Math.max(...cubeDataArray.map((cube) => cube.width))
      const maxHeight = Math.max(...cubeDataArray.map((cube) => cube.height))

      // Analyze the actual spacing in the DOM
      // We'll measure the distance between consecutive cubes to determine the exact spacing ratio
      let actualSpacingRatio = 0.45 // Default fallback value

      if (cubeDataArray.length > 1) {
        // Find two consecutive cubes to measure the spacing
        const bottomPositions = cubeDataArray.map((cube) => cube.bottomPosition)

        // Calculate the average spacing ratio from all consecutive pairs
        let totalRatio = 0
        let pairCount = 0

        for (let i = 0; i < bottomPositions.length - 1; i++) {
          const currentBottom = bottomPositions[i]
          const nextBottom = bottomPositions[i + 1]
          const distance = Math.abs(currentBottom - nextBottom)

          // Only use valid measurements
          if (distance > 0 && maxHeight > 0) {
            const ratio = distance / maxHeight
            totalRatio += ratio
            pairCount++
          }
        }

        // Calculate the average if we have valid measurements
        if (pairCount > 0) {
          actualSpacingRatio = totalRatio / pairCount
          console.log(`Measured spacing ratio: ${actualSpacingRatio}`)
        }
      }

      // Set canvas dimensions with generous padding
      const padding = 100
      const canvasWidth = (maxWidth + padding * 2) * 2
      const canvasHeight = (totalTowerHeight(cubeDataArray.length, maxHeight, actualSpacingRatio) + padding * 2) * 2

      // Create canvas
      const canvas = document.createElement("canvas")
      canvas.width = canvasWidth
      canvas.height = canvasHeight

      const ctx = canvas.getContext("2d")
      if (!ctx) {
        throw new Error("Could not get canvas context")
      }

      // Fill with white background
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, canvasWidth, canvasHeight)

      // Calculate positions for each cube in the tower (bottom to top)
      const positions = calculateCubePositions(
        cubeDataArray.length,
        maxHeight,
        actualSpacingRatio,
        canvasHeight,
        padding,
      )

      // Load all images with proper CORS handling
      const loadImagePromises: Promise<LoadedImageData | null>[] = cubeDataArray.map((cube, index) => {
        return new Promise((resolve) => {
          const img = new Image()
          img.crossOrigin = "anonymous"

          img.onload = () => {
            const imageData: LoadedImageData = {
              img: img,
              x: (padding + (maxWidth - cube.width) / 2) * 2, // 2x scaling
              y: positions[index], // Already includes 2x scaling
              width: cube.width * 2, // 2x scaling
              height: cube.height * 2, // 2x scaling
              zIndex: cube.zIndex,
            }
            resolve(imageData)
          }

          img.onerror = () => {
            console.error(`Failed to load image: ${cube.src}`)
            // Try to create a placeholder
            const placeholderImg = new Image()
            placeholderImg.onload = () => {
              const imageData: LoadedImageData = {
                img: placeholderImg,
                x: (padding + (maxWidth - cube.width) / 2) * 2, // 2x scaling
                y: positions[index], // Already includes 2x scaling
                width: cube.width * 2, // 2x scaling
                height: cube.height * 2, // 2x scaling
                zIndex: cube.zIndex,
              }
              resolve(imageData)
            }
            placeholderImg.onerror = () => resolve(null)
            placeholderImg.src = `/placeholder.svg?height=${Math.round(cube.height)}&width=${Math.round(cube.width)}&query=${encodeURIComponent(cube.alt)}`
          }

          img.src = cube.src
        })
      })

      // Wait for all images to load
      const loadedImages = await Promise.all(loadImagePromises)

      // Filter out null values and sort by z-index (draw in correct order)
      const validImages = loadedImages
        .filter((img): img is LoadedImageData => img !== null)
        .sort((a, b) => a.zIndex - b.zIndex) // Lower z-index drawn first (base cube first)

      // Draw images on canvas in correct z-order
      for (const imageData of validImages) {
        ctx.drawImage(imageData.img, imageData.x, imageData.y, imageData.width, imageData.height)
      }

      // Add watermark
      addWatermark(ctx, canvasWidth, canvasHeight)

      // Return the data URL
      return canvas.toDataURL("image/png")
    } catch (error) {
      console.error("Error generating image:", error)
      return null
    }
  }

  // Helper function to calculate the total tower height
  const totalTowerHeight = (cubeCount: number, cubeHeight: number, spacingRatio: number): number => {
    if (cubeCount <= 0) return 0
    if (cubeCount === 1) return cubeHeight

    // For multiple cubes, calculate the height with overlap
    return cubeHeight + (cubeCount - 1) * (cubeHeight * spacingRatio)
  }

  // Helper function to calculate cube positions
  const calculateCubePositions = (
    cubeCount: number,
    cubeHeight: number,
    spacingRatio: number,
    canvasHeight: number,
    padding: number,
  ): number[] => {
    const positions: number[] = []
    const totalHeight = totalTowerHeight(cubeCount, cubeHeight, spacingRatio)

    // Start position for the base cube (at the bottom)
    const baseY = canvasHeight - padding * 2 - cubeHeight * 2

    for (let i = 0; i < cubeCount; i++) {
      if (i === 0) {
        // Base cube at the bottom
        positions.push(baseY)
      } else {
        // Each subsequent cube is positioned above the previous one
        const offset = cubeHeight * spacingRatio * 2 // 2x scaling
        positions.push(positions[i - 1] - offset)
      }
    }

    return positions
  }

  const handleDownload = async () => {
    if (isDownloading) return

    // Track download image event
    track("DownloadImage", {
      walletAddress: walletAddress.substring(0, 6) + "...",
      collectionsCount: collections.length,
    })

    setIsDownloading(true)

    try {
      const dataUrl = await generateImage()

      if (!dataUrl) {
        throw new Error("Failed to generate image")
      }

      setImageUrl(dataUrl)

      if (isMobile || isIOSDevice) {
        // For mobile devices, show a dialog with instructions
        setShowDownloadDialog(true)
      } else {
        // For desktop, proceed with direct download
        const link = document.createElement("a")
        link.download = `nft-collections-${walletAddress.substring(0, 6)}.png`
        link.href = dataUrl
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error("Error downloading image:", error)
      // Show dialog with error message
      setShowDownloadDialog(true)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleTwitterShare = () => {
    // Track share on X event
    track("ShareOnX", {
      walletAddress: walletAddress.substring(0, 6) + "...",
      hasEns: !!ensName,
    })

    const shareUrl = `${window.location.origin}/results?wallet=${encodeURIComponent(walletAddress)}`
    const text = ensName
      ? `Check out the NFTOTEM for ${ensName} (${walletAddress.substring(0, 6)}...) #NFTOTEM`
      : `Check out these NFTOTEM for wallet ${walletAddress.substring(0, 6)}... #NFTOTEM`
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`
    window.open(twitterUrl, "_blank")
  }

  const handleWarpcastShare = () => {
    // Track share on Warpcast event
    track("ShareOnWarpcast", {
      walletAddress: walletAddress.substring(0, 6) + "...",
      hasEns: !!ensName,
    })

    const shareUrl = `${window.location.origin}/results?wallet=${encodeURIComponent(walletAddress)}`
    const text = ensName
      ? `Check out the NFTOTEM for ${ensName} (${walletAddress.substring(0, 6)}...) #NFTOTEM\n\n${shareUrl}`
      : `Check out these NFTOTEM for wallet ${walletAddress.substring(0, 6)}... #NFTOTEM\n\n${shareUrl}`

    const warpcastUrl = `https://warpcast.com/~/compose?text=${encodeURIComponent(text)}`
    window.open(warpcastUrl, "_blank")
  }

  // Compact one-row mode (used in Options rows on both desktop and mobile)
  if (compact) {
    return (
      <>
        <div className="flex">
          <Button
            onClick={handleDownload}
            disabled={isDownloading || collections.length === 0}
            variant="outline"
            className="rounded-full w-10 h-10 p-0 flex items-center justify-center"
            aria-label="Download Image"
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </Button>

          <Button
            onClick={handleTwitterShare}
            disabled={collections.length === 0}
            className="bg-black hover:bg-gray-800 rounded-full w-10 h-10 p-0 flex items-center justify-center ml-2"
            aria-label="Share on X (Twitter)"
          >
            <XIcon size={18} />
          </Button>

          <Button
            onClick={handleWarpcastShare}
            disabled={collections.length === 0}
            className="bg-[#472A91] hover:bg-[#3B2277] rounded-full w-10 h-10 p-0 flex items-center justify-center ml-2"
            aria-label="Share on Warpcast"
          >
            <WarpcastIcon size={20} />
          </Button>
        </div>

        {/* Download Dialog for Desktop */}
        <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Download Image</DialogTitle>
              <DialogDescription>
                {!imageUrl
                  ? "Failed to generate image. Please try again."
                  : "Your image has been generated successfully."}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col items-center justify-center p-4 overflow-auto max-h-[70vh]">
              {imageUrl ? (
                <a
                  href={imageUrl}
                  download={`nft-collections-${walletAddress.substring(0, 6)}.png`}
                  className="block max-w-full border rounded-lg overflow-hidden"
                  onClick={() => setTimeout(() => setShowDownloadDialog(false), 1000)}
                >
                  <img src={imageUrl || "/placeholder.svg"} alt="NFT Collections" className="max-w-full h-auto" />
                </a>
              ) : (
                <div className="text-center text-gray-500">
                  <p>Image generation failed. Please try again.</p>
                  <Button
                    onClick={() => {
                      setShowDownloadDialog(false)
                      handleDownload()
                    }}
                    className="mt-4"
                  >
                    Retry
                  </Button>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-2 sticky bottom-0 bg-background pt-2">
              <DialogClose asChild>
                <Button variant="outline">Close</Button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  // Mobile layout - includes download button and social buttons
  if (isMobile) {
    return (
      <>
        <Button
          onClick={handleDownload}
          disabled={isDownloading || collections.length === 0}
          className="bg-[#7B39ED] hover:bg-[#6429D8] rounded-lg w-10 h-10 p-0 flex items-center justify-center"
          aria-label="Download Image"
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin text-white" />
          ) : (
            <Download className="h-4 w-4 text-white" />
          )}
        </Button>

        <Button
          onClick={handleTwitterShare}
          disabled={collections.length === 0}
          className="bg-black hover:bg-gray-800 rounded-lg w-10 h-10 p-0 flex items-center justify-center"
          aria-label="Share on X (Twitter)"
        >
          <XIcon size={18} className="text-white" />
        </Button>

        <Button
          onClick={handleWarpcastShare}
          disabled={collections.length === 0}
          className="bg-[#472A91] hover:bg-[#3B2277] rounded-lg w-10 h-10 p-0 flex items-center justify-center"
          aria-label="Share on Warpcast"
        >
          <WarpcastIcon size={20} />
        </Button>

        {/* Download Dialog for Mobile Devices */}
        <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Download Image</DialogTitle>
              <DialogDescription>
                {!imageUrl
                  ? "Failed to generate image. Please try again."
                  : isIOSDevice
                    ? "To save this image on your iOS device, tap and hold the image below, then select 'Add to Photos'."
                    : "Tap the image below to download it to your device."}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col items-center justify-center p-4 overflow-auto max-h-[70vh]">
              {imageUrl ? (
                <a
                  href={imageUrl}
                  download={`nft-collections-${walletAddress.substring(0, 6)}.png`}
                  className="block max-w-full border rounded-lg overflow-hidden"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => !isIOSDevice && setTimeout(() => setShowDownloadDialog(false), 1000)}
                >
                  <img src={imageUrl || "/placeholder.svg"} alt="NFT Collections" className="max-w-full h-auto" />
                </a>
              ) : (
                <div className="text-center text-gray-500">
                  <p>Image generation failed. Please try again.</p>
                  <Button
                    onClick={() => {
                      setShowDownloadDialog(false)
                      handleDownload()
                    }}
                    className="mt-4"
                  >
                    Retry
                  </Button>
                </div>
              )}

              {isIOSDevice && imageUrl && (
                <p className="mt-4 text-sm text-gray-500 text-center">
                  If you're having trouble, you can also take a screenshot of this image.
                </p>
              )}
            </div>

            <div className="flex justify-end mt-2 sticky bottom-0 bg-background pt-2">
              <DialogClose asChild>
                <Button variant="outline">Close</Button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  // Desktop layout - Download button and social buttons
  return (
    <div className="flex flex-col gap-4">
      {/* Download and Share buttons in one row */}
      <div className="flex">
        <Button
            onClick={handleDownload}
            disabled={isDownloading || collections.length === 0}
          className="bg-[#7B39ED] hover:bg-[#6429D8] rounded-lg w-10 h-10 p-0 flex items-center justify-center"
          aria-label="Download Image"
          >
              {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin text-white" />
              ) : (
            <Download className="h-4 w-4 text-white" />
              )}
        </Button>

        <Button
          onClick={handleTwitterShare}
          disabled={collections.length === 0}
          className="bg-black hover:bg-gray-800 rounded-lg w-10 h-10 p-0 flex items-center justify-center ml-2"
          aria-label="Share on X (Twitter)"
        >
          <XIcon size={18} />
        </Button>

        <Button
          onClick={handleWarpcastShare}
          disabled={collections.length === 0}
          className="bg-[#472A91] hover:bg-[#3B2277] rounded-lg w-10 h-10 p-0 flex items-center justify-center ml-2"
          aria-label="Share on Warpcast"
        >
          <WarpcastIcon size={20} />
        </Button>
      </div>

      {/* Download Dialog for Desktop */}
      <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Download Image</DialogTitle>
            <DialogDescription>
              {!imageUrl
                ? "Failed to generate image. Please try again."
                : "Your image has been generated successfully."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center p-4 overflow-auto max-h-[70vh]">
            {imageUrl ? (
              <a
                href={imageUrl}
                download={`nft-collections-${walletAddress.substring(0, 6)}.png`}
                className="block max-w-full border rounded-lg overflow-hidden"
                onClick={() => setTimeout(() => setShowDownloadDialog(false), 1000)}
              >
                <img src={imageUrl || "/placeholder.svg"} alt="NFT Collections" className="max-w-full h-auto" />
              </a>
            ) : (
              <div className="text-center text-gray-500">
                <p>Image generation failed. Please try again.</p>
                <Button
                  onClick={() => {
                    setShowDownloadDialog(false)
                    handleDownload()
                  }}
                  className="mt-4"
                >
                  Retry
                </Button>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-2 sticky bottom-0 bg-background pt-2">
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
